import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Rating
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import SpeedIcon from '@mui/icons-material/Speed';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import { Agent } from '../types/Agent';
import { api } from '../services/api';
import { Link } from 'react-router-dom';
import HuggingFaceAgent from '../components/agent-specific/HuggingFaceAgent';

// Styles personnalisés avec une meilleure organisation
const PageBackground = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: '#f5f5f5',
  minHeight: '100vh',
  paddingBottom: theme.spacing(8),
  paddingTop: '100px',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(74, 111, 220, 0.05) 0%, transparent 25%), radial-gradient(circle at 80% 70%, rgba(74, 111, 220, 0.05) 0%, transparent 25%)',
    backgroundSize: '100% 100%',
    zIndex: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'linear-gradient(45deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 100%)',
    backgroundSize: '400% 400%',
    animation: 'gradientAnimation 15s ease infinite',
    zIndex: -1,
  },
  '@keyframes gradientAnimation': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
}));

// const FuturisticGrid = styled(Box)(({ theme }) => ({
//   position: 'absolute',
//   top: 0,
//   left: 0,
//   right: 0,
//   bottom: 0,
//   backgroundImage: `
//     linear-gradient(rgba(74, 111, 220, 0.03) 1px, transparent 1px),
//     linear-gradient(90deg, rgba(74, 111, 220, 0.03) 1px, transparent 1px)
//   `,
//   backgroundSize: '40px 40px',
//   pointerEvents: 'none',
//   zIndex: 0,
// }));

const ContentContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
}));

const PageHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #333333 0%, #1a1a1a 100%)`,
  padding: theme.spacing(8, 0),
  borderRadius: theme.shape.borderRadius * 2,
  marginBottom: theme.spacing(6),
  color: '#ffffff',
  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    right: '-50%',
    bottom: '-50%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
    zIndex: 0,
  }
}));

const PricingSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(249,250,251,0.9) 100%)',
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(6),
  marginBottom: theme.spacing(8),
  boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #8B5CF6 0%, #6366F1 100%)',
  }
}));

const PriceDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(4),
}));

const DetailedSection = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(6),
  marginBottom: theme.spacing(6),
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
}));

const BenefitCard = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateX(8px)',
  }
}));

const FeatureBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  backdropFilter: 'blur(5px)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
  },
}));

const ComingSoonBanner = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #333333 0%, #1a1a1a 100%)`,
  padding: theme.spacing(8, 0),
  borderRadius: theme.shape.borderRadius * 2,
  marginBottom: theme.spacing(6),
  color: '#ffffff',
  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    right: '-50%',
    bottom: '-50%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
    zIndex: 0,
  }
}));

// Tableau des icônes pour les fonctionnalités
const featureIcons = [
  { icon: <AutorenewIcon fontSize="large" />, title: "Automatisation", desc: "Gestion autonome des échanges avec les prospects et suivi personnalisé" },
  { icon: <EmojiObjectsIcon fontSize="large" />, title: "Personnalisation", desc: "Adaptation dynamique du discours selon le profil et les intérêts du prospect" },
  { icon: <CheckCircleOutlineIcon fontSize="large" />, title: "Qualification", desc: "Identification précise des prospects à fort potentiel avec scoring intelligent" },
  { icon: <SpeedIcon fontSize="large" />, title: "Analytics", desc: "Tableaux de bord et rapports détaillés sur les performances commerciales" },
  { icon: <ScheduleIcon fontSize="large" />, title: "Disponibilité", desc: "Service continu 24/7 sans interruption avec réponses instantanées" },
  { icon: <IntegrationInstructionsIcon fontSize="large" />, title: "Intégration", desc: "Compatible avec vos outils CRM existants et déploiement rapide" }
];

const AgentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const data = await api.getAgentById(id || '');
        setAgent(data);
      } catch (err) {
        setError('Erreur lors du chargement de l\'agent');
      } finally {
        setLoading(false);
      }
    };

    fetchAgent();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !agent) {
    return (
      <Container>
        <Alert severity="error">{error || 'Agent non trouvé'}</Alert>
      </Container>
    );
  }

  // Vérifie si c'est un agent spécial qui nécessite un composant spécifique
  const renderSpecificAgentContent = () => {
    switch (agent._id) {
      case 'huggingface-deepsite':
        return <HuggingFaceAgent />;
      // Ajoutez d'autres cas pour d'autres agents spéciaux ici
      default:
        return null;
    }
  };

  return (
    <PageBackground>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {agent.name}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {agent.role}
              </Typography>
              <Box sx={{ my: 3 }}>
                <img
                  src={agent.image}
                  alt={agent.name}
                  style={{ width: '100%', maxHeight: '400px', objectFit: 'cover', borderRadius: '8px' }}
                />
              </Box>
              <Typography variant="body1" paragraph>
                {agent.description}
              </Typography>
              
              {/* Intégration du composant spécifique à l'agent */}
              {renderSpecificAgentContent()}

              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Spécifications
                </Typography>
                <List>
                  {agent.specifications.map((spec, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={spec} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 4, position: 'sticky', top: 20 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {agent.price}€
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Rating value={agent.rating} readOnly />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({agent.reviews} avis)
                  </Typography>
                </Box>
              </Box>
              
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                component={Link}
                to={`/agents/${agent._id}/test`}
                sx={{ mb: 2 }}
              >
                Tester l'agent
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                size="large"
                component={Link}
                to={`/agents/${agent._id}/hire`}
              >
                Engager l'agent
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </PageBackground>
  );
};

export default AgentDetail;